import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { TestIntro } from '../components/TestIntro';
import { paperTestSummary } from '../lib/paperTestPages';
import { Skeleton } from '../components/Skeleton';
import { PushOptIn } from '../components/PushOptIn';
import { pushTopicLabels } from '../lib/pushTopics';
import { useListingRadar } from '../hooks/useListingRadar';
import { ApiClientError, type ListingRadarResponse } from '../lib/apiClient';
import {
  LISTING_COSTS,
  LISTING_RULE,
  type Listing,
  type ListingVerdict,
} from '../lib/listingRadar';
import { netReturn } from '../lib/crashBounce';
import { formatSignedUsd } from '../lib/format';
// Shares the paper-test card layout with Crash Bounce and Breakout Watch.
import base from './CrashBouncePage.module.css';

const pct = (x: number | null, digits = 1) =>
  x === null ? '—' : `${(x * 100).toFixed(digits)}%`;
const signedPct = (x: number | null, digits = 1) =>
  x === null
    ? '—'
    : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x * 100).toFixed(digits)}%`;
const price = (x: number | null) =>
  x === null
    ? '—'
    : `$${x.toLocaleString(undefined, { maximumSignificantDigits: 4, minimumSignificantDigits: 2 })}`;
const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
const rel = (px: number | null, entry: number | null) =>
  px === null || entry === null ? null : px / entry - 1;

const VERDICT_LABEL: Record<ListingVerdict, string> = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
};

export function ListingRadarPage() {
  const query = useListingRadar();
  const data = query.data;
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Listing radar data is temporarily unavailable.';
  return (
    <div className={base.page}>
      <TestIntro
        heading={
          <div className={base.heading}>
            <h1>Listing Radar</h1>
            <RefreshStatus
              updatedAt={query.dataUpdatedAt}
              isFetching={query.isFetching}
              isError={query.isError}
              onRefresh={() => query.refetch()}
            />
          </div>
        }
        summary={paperTestSummary('/listing-radar')}
      >
        <p>
          &ldquo;Coins pump when Coinbase lists them.&rdquo; This checks it. Every hour it
          reads Coinbase&apos;s list of trading pairs. When a new USD pair opens for
          trading, it paper-buys at the next full hour&apos;s open and sells 7 days later.
          The rules were written down before the first listing was seen.
        </p>
      </TestIntro>
      <Disclaimer
        summary="Paper only — not a recommendation."
        text="Paper only. Nothing on this page places an order. New listings are among the most volatile things in crypto, and much of any listing move often happens before trading opens, where this test can't buy."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Loading the listing radar…
          </p>
          <Skeleton width="100%" height="8rem" />
          <Skeleton width="100%" height="10rem" />
        </>
      )}
      {query.isError && !data && <p role="alert">{error}</p>}
      {data && (
        <>
          <Scorecard data={data} />
          <PushOptIn topic="listing-radar" {...pushTopicLabels('listing-radar')} />
          <Announced rows={data.active.filter((l) => l.tradableSeenMs === null)} />
          <Open rows={data.active.filter((l) => l.tradableSeenMs !== null)} />
          <Closed rows={data.closed} />
          <p className={base.fine}>
            Product list and hourly prices come from Coinbase&apos;s public API, checked
            seven minutes after each hour. A pair counts when its quote currency is USD,
            it isn&apos;t a stablecoin pair, and it first appears after the radar started;
            it&apos;s bought once orders can match (not post-only, cancel-only or in
            auction). Each trade is scored with {pct(LISTING_COSTS.slippagePerSide, 2)}{' '}
            slippage per fill and a {pct(LISTING_COSTS.feeHigh, 1)} fee per side. Rules
            locked {dayTime(Date.parse(data.registeredAt))}.{' '}
            {data.seededAtMs
              ? `Watching ${data.knownProducts.toLocaleString()} existing pairs since ${day(data.seededAtMs)}.`
              : 'Waiting for the first hourly check.'}
            {data.lastRunMs && ` Last checked ${dayTime(data.lastRunMs)}.`}
            {data.lastError &&
              " The product list couldn't be read on that check; open trades still updated."}
          </p>
        </>
      )}
    </div>
  );
}

function Scorecard({ data }: { data: ListingRadarResponse }) {
  const s = data.summary;
  const bar = data.passBar;
  return (
    <section className={base.card} aria-labelledby="lr-score-heading">
      <div className={base.cardHead}>
        <h2 id="lr-score-heading">Forward test: buy and hold 7 days</h2>
        <span className={`${base.verdict} ${base[s.verdict]}`}>
          {VERDICT_LABEL[s.verdict]}
        </span>
      </div>
      <p className={base.progress}>
        {s.listings}/{bar.minListings} listings · {s.weeks}/{bar.minWeeks} weeks
        {s.open > 0 && ` · ${s.open} open`}
        {s.announced > 0 && ` · ${s.announced} waiting to trade`}
      </p>
      {s.listings > 0 && (
        <dl className={base.stats}>
          <div>
            <dt>Win rate</dt>
            <dd>{pct(s.winRate)}</dd>
          </div>
          <div>
            <dt>Avg 7-day</dt>
            <dd>{signedPct(s.meanNetHigh)}</dd>
          </div>
          <div>
            <dt>${data.stakeDollars.toLocaleString()}/trade P&amp;L</dt>
            <dd>{formatSignedUsd(s.pnlHigh)}</dd>
          </div>
        </dl>
      )}
      <p className={base.small}>
        {s.listings > 0 ? (
          <>
            Before fees, the average move was {signedPct(s.mean1h)} after the first hour
            and {signedPct(s.mean24h)} after a day.{' '}
            {s.intervalHigh &&
              `Likely range of the 7-day average after fees: ${signedPct(s.intervalHigh[0])} to ${signedPct(s.intervalHigh[1])}.`}
          </>
        ) : (
          'No finished listings yet. Coinbase adds a few new USD pairs a month, so this takes a while.'
        )}
      </p>
      <p className={base.small}>
        Passes after {bar.minListings} listings across {bar.minWeeks} separate weeks if
        the average 7-day trade is above zero at a {pct(bar.feePerSide, 1)} fee and the
        low end of its likely range is too.
      </p>
    </section>
  );
}

function Announced({ rows }: { rows: Listing[] }) {
  if (rows.length === 0) return null;
  return (
    <section className={base.card} aria-labelledby="lr-announced-heading">
      <h2 id="lr-announced-heading">Listed, not trading yet</h2>
      <p className={base.small}>
        Coinbase usually opens a new book in post-only or auction mode first. The paper
        buy waits until orders can match.
      </p>
      <ul className={base.list}>
        {rows.map((l) => (
          <li key={l.productId} className={base.row}>
            <div className={base.rowHead}>
              <strong>{l.productId}</strong>
              <span className={base.flat}>Waiting</span>
            </div>
            <p className={base.small}>First seen {dayTime(l.firstSeenMs)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Open({ rows }: { rows: Listing[] }) {
  if (rows.length === 0) return null;
  return (
    <section className={base.card} aria-labelledby="lr-open-heading">
      <h2 id="lr-open-heading">Open paper trades</h2>
      <ul className={base.list}>
        {rows.map((l) => {
          const now = rel(l.lastClose, l.entryPx);
          return (
            <li key={l.productId} className={base.row}>
              <div className={base.rowHead}>
                <strong>{l.productId}</strong>
                <span className={tone(now)}>{signedPct(now)}</span>
              </div>
              <p className={base.small}>
                {l.entryMs === null
                  ? `Opened ${dayTime(l.tradableSeenMs!)} · buying at the next hour's open`
                  : `Bought ${dayTime(l.entryMs)} at ${price(l.entryPx)} · hour ${l.bars} of ${LISTING_RULE.holdBars} · best ${signedPct(rel(l.maxHigh, l.entryPx))} · worst ${signedPct(rel(l.minLow, l.entryPx))}`}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Closed({ rows }: { rows: Listing[] }) {
  return (
    <section className={base.card} aria-labelledby="lr-closed-heading">
      <h2 id="lr-closed-heading">Finished listings</h2>
      {rows.length === 0 ? (
        <p className={base.small}>None yet.</p>
      ) : (
        <ul className={base.list}>
          {rows.slice(0, 50).map((l) => {
            const net = netReturn(l.entryPx!, l.exitPx!, LISTING_COSTS.feeHigh);
            return (
              <li key={`${l.productId}-${l.entryMs}`} className={base.row}>
                <div className={base.rowHead}>
                  <strong>{l.productId}</strong>
                  <span className={tone(net)}>{signedPct(net)}</span>
                </div>
                <p className={base.small}>
                  {day(l.entryMs!)} → {day(l.exitMs!)} · 1h{' '}
                  {signedPct(rel(l.close1, l.entryPx))} · 24h{' '}
                  {signedPct(rel(l.close24, l.entryPx))}
                  {l.incomplete && ' · stopped trading before 7 days'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function tone(x: number | null) {
  if (x === null || x === 0) return base.flat;
  return x > 0 ? base.up : base.down;
}
